import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useRef,
} from 'react'

const NotificationContext = createContext(null)

const NotificationActionTypes = {
  ADD: 'add',
  REMOVE: 'remove',
  CLEAR: 'clear',
}

const notificationReducer = (state, action) => {
  switch (action.type) {
    case NotificationActionTypes.ADD:
      return [...state, action.payload]
    case NotificationActionTypes.REMOVE:
      return state.filter((notification) => notification.id !== action.payload)
    case NotificationActionTypes.CLEAR:
      return []
    default:
      return state
  }
}

const createIdentifier = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `notification-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export const NotificationProvider = ({ children }) => {
  const [notifications, dispatch] = useReducer(notificationReducer, [])
  const timersRef = useRef(new Map())

  const dismiss = useCallback((id) => {
    const timer = timersRef.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timersRef.current.delete(id)
    }
    dispatch({ type: NotificationActionTypes.REMOVE, payload: id })
  }, [])

  const clear = useCallback(() => {
    for (const timer of timersRef.current.values()) {
      clearTimeout(timer)
    }
    timersRef.current.clear()
    dispatch({ type: NotificationActionTypes.CLEAR })
  }, [])

  const notify = useCallback((notification) => {
    const id = notification.id ?? createIdentifier()
    const payload = {
      id,
      type: notification.type ?? 'info',
      title: notification.title ?? '',
      message: notification.message ?? '',
      autoDismissMs: notification.autoDismissMs ?? 6000,
      createdAt: Date.now(),
    }

    dispatch({ type: NotificationActionTypes.ADD, payload })

    if (payload.autoDismissMs) {
      const timer = setTimeout(() => {
        dismiss(id)
      }, payload.autoDismissMs)
      timersRef.current.set(id, timer)
    }

    return id
  }, [dismiss])

  const value = useMemo(
    () => ({
      notifications,
      notify,
      dismiss,
      clear,
    }),
    [notifications, notify, dismiss, clear],
  )

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
}

export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}

export const NotificationTypes = {
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
}
